import { useRegisterSW } from "@/lib/pwa/pwa-update";
import { toast } from "sonner";
import * as React from "react";

const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000; // 1 hour

/**
 * Mount once near the root of the app to:
 * - register the SW
 * - show a prompt when a new version is available
 * - periodically check for updates + on foreground
 */
export function usePwaUpdate(): void {
  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_url, registration) {
      if (!registration) return;
      setInterval(() => {
        void registration.update();
      }, UPDATE_CHECK_INTERVAL_MS);
    },
    onRegisterError(error: unknown) {
      console.error("[pwa] service worker registration error", error);
    },
  });

  const updateSw = React.useRef(updateServiceWorker);
  updateSw.current = updateServiceWorker;

  React.useEffect(() => {
    if (!needRefresh) return;

    toast.info("Update available", {
      description: "Reload to get the latest version.",
      duration: Infinity,
      action: {
        label: "Reload",
        onClick: () => void updateSw.current(true),
      },
      cancel: {
        label: "Later",
        onClick: () => {},
      },
    });
  }, [needRefresh]);

  React.useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      void navigator.serviceWorker
        ?.getRegistration()
        .then((r) => r?.update())
        .catch(() => {});
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", onVisibilityChange);
  }, []);
}
